import { useEffect, useState } from "react";
import { Badge } from "@zweb/ui";
import { apiFetch } from "../lib/api";

interface Schedule {
  id: string;
  title: string;
  description: string;
  type: string;
  status: string;
  startAt: string;
  endAt: string;
  location: string | null;
  meetingUrl: string | null;
}

const typeLabels: Record<string, string> = {
  meeting: "Reunião",
  demo: "Demonstração",
  training: "Treinamento",
  deployment: "Implantação",
  call: "Chamada",
  other: "Outro",
};

const statusLabels: Record<string, string> = {
  scheduled: "Agendado",
  confirmed: "Confirmado",
  completed: "Concluído",
  cancelled: "Cancelado",
  rescheduled: "Reagendado",
};

const statusColors: Record<string, string> = {
  scheduled: "bg-accent-100 text-accent-800",
  confirmed: "bg-green-100 text-green-800",
  completed: "bg-gray-100 text-gray-800",
  cancelled: "bg-red-100 text-red-800",
  rescheduled: "bg-yellow-100 text-yellow-800",
};

export default function SchedulePage() {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewSchedule, setShowNewSchedule] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("meeting");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [location, setLocation] = useState("");
  const [meetingUrl, setMeetingUrl] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return;
    }

    apiFetch("/api/schedules")
      .then((json) => {
        if (json.success) {
          setSchedules(json.data);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const json = await apiFetch("/api/schedules", {
        method: "POST",
        body: JSON.stringify({
          title,
          description,
          type,
          startAt,
          endAt,
          location: location || undefined,
          meetingUrl: meetingUrl || undefined,
        }),
      });
      if (json.success) {
        setShowNewSchedule(false);
        setTitle("");
        setDescription("");
        setType("meeting");
        setStartAt("");
        setEndAt("");
        setLocation("");
        setMeetingUrl("");
        window.location.reload();
      }
    } catch (error) {
      console.error("Erro ao criar agendamento:", error);
    }
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const groupByDate = (schedules: Schedule[]) => {
    const groups: Record<string, Schedule[]> = {};
    schedules.forEach((schedule) => {
      const date = new Date(schedule.startAt).toLocaleDateString("pt-BR");
      if (!groups[date]) groups[date] = [];
      groups[date].push(schedule);
    });
    return groups;
  };

  const groupedSchedules = groupByDate(schedules);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/dashboard" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/dashboard" className="text-sm font-medium hover:text-accent-600">Dashboard</a>
            <a href="/chamados" className="text-sm font-medium hover:text-accent-600">Chamados</a>
            <a href="/downloads" className="text-sm font-medium hover:text-accent-600">Downloads</a>
            <a href="/licencas" className="text-sm font-medium hover:text-accent-600">Licenças</a>
            <a href="/cronograma" className="text-sm font-medium text-accent-600">Cronograma</a>
            <button
              onClick={() => {
                localStorage.removeItem("token");
                window.location.href = "/";
              }}
              className="text-sm text-red-600 hover:underline"
            >
              Sair
            </button>
          </nav>
        </div>
      </header>

      <main className="container py-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Cronograma</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Gerencie seus agendamentos e compromissos
            </p>
          </div>
          <button
            onClick={() => setShowNewSchedule(true)}
            className="rounded-lg bg-accent-600 px-4 py-2 font-medium text-white hover:bg-accent-700"
          >
            Novo Agendamento
          </button>
        </div>

        {showNewSchedule && (
          <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
            <h2 className="text-xl font-semibold">Novo Agendamento</h2>
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium">Título</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Descrição</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium">Tipo</label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                  >
                    <option value="meeting">Reunião</option>
                    <option value="demo">Demonstração</option>
                    <option value="training">Treinamento</option>
                    <option value="deployment">Implantação</option>
                    <option value="call">Chamada</option>
                    <option value="other">Outro</option>
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Local</label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    placeholder="Endereço ou link"
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium">Início</label>
                  <input
                    type="datetime-local"
                    value={startAt}
                    onChange={(e) => setStartAt(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Fim</label>
                  <input
                    type="datetime-local"
                    value={endAt}
                    onChange={(e) => setEndAt(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">URL da Reunião</label>
                <input
                  type="url"
                  value={meetingUrl}
                  onChange={(e) => setMeetingUrl(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                  placeholder="https://meet.google.com/..."
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  className="rounded-lg bg-accent-600 px-4 py-2 font-medium text-white hover:bg-accent-700"
                >
                  Criar Agendamento
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewSchedule(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 font-medium hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-900"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="mt-8 space-y-8">
          {Object.keys(groupedSchedules).length === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-950">
              <p className="text-gray-600 dark:text-gray-400">
                Nenhum agendamento encontrado. Clique em "Novo Agendamento" para criar um.
              </p>
            </div>
          ) : (
            Object.entries(groupedSchedules)
              .sort(([a], [b]) => new Date(a.split("/").reverse().join("-")).getTime() - new Date(b.split("/").reverse().join("-")).getTime())
              .map(([date, daySchedules]) => (
                <div key={date}>
                  <h2 className="text-lg font-semibold text-gray-600 dark:text-gray-400">
                    {date}
                  </h2>
                  <div className="mt-4 space-y-4">
                    {daySchedules.map((schedule) => (
                      <div
                        key={schedule.id}
                        className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-lg font-semibold">{schedule.title}</h3>
                            {schedule.description && (
                              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                {schedule.description}
                              </p>
                            )}
                            <div className="mt-3 flex items-center gap-2">
                              <Badge className={statusColors[schedule.status]}>
                                {statusLabels[schedule.status]}
                              </Badge>
                              <Badge variant="secondary">{typeLabels[schedule.type]}</Badge>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium">
                              {formatDateTime(schedule.startAt)}
                            </div>
                            <div className="text-sm text-gray-500">
                              até {formatDateTime(schedule.endAt)}
                            </div>
                          </div>
                        </div>

                        {(schedule.location || schedule.meetingUrl) && (
                          <div className="mt-4 flex items-center gap-4 text-sm">
                            {schedule.location && (
                              <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                {schedule.location}
                              </div>
                            )}
                            {schedule.meetingUrl && (
                              <a
                                href={schedule.meetingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-accent-600 hover:underline"
                              >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Entrar na reunião
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))
          )}
        </div>
      </main>
    </div>
  );
}
